// Which Settings tab, if any, some other surface has asked the shell to open.
//
// The shell already honours `?settings=connections`, but ONLY at mount — that contract exists for the OAuth
// popup-blocked FALLBACK (`oauthPopup.ts`), which returns as a cold full-page load, so reading the search
// param once in a useState initialiser is enough there. An in-app button is the opposite case: the shell is
// already mounted, so setting the param would do nothing, and forcing a reload to make it work would throw
// away the page state the band is standing in.
//
// So: a one-field store, the `toasts.ts` shape — UI state only, imperatively settable from any depth, no
// prop-threading and no second context provider. The shell subscribes and clears it once consumed, which is
// what makes it a REQUEST rather than a second source of truth for "is settings open".
import { create } from "zustand";
import { registerStoreReset } from "./stores.js";
import type { SettingsTab } from "../features/settings/settingsTabs.js";

interface SettingsPanelState {
  requestedTab: SettingsTab | null;
  request: (tab: SettingsTab) => void;
  consume: () => void;
  reset: () => void;
}

export const useSettingsPanel = create<SettingsPanelState>((set) => ({
  requestedTab: null,
  request: (tab) => set({ requestedTab: tab }),
  consume: () => set({ requestedTab: null }),
  reset: () => set({ requestedTab: null }),
}));

// Scorch on band switch / logout — a pending "open connections" from the previous tenant must not fire over
// the new one (stores.ts).
registerStoreReset(() => useSettingsPanel.getState().reset());

/** Imperative opener for non-component call sites. */
export function openSettingsTab(tab: SettingsTab): void {
  useSettingsPanel.getState().request(tab);
}
